<template>
  <div class="pie-chart">
    <svg height="200" width="200" viewBox="0 0 200 200" class="donut-chart">
      <g v-for="(value, index) in sortedValues" v-bind:key="index">
        <circle
          :cx="cx"
          :cy="cy"
          :r="radius"
          :stroke="colors[index]"
          :stroke-width="strokeWidth"
          :stroke-dasharray="adjustedCircumference"
          :stroke-dashoffset="calculateStrokeDashOffset(value, circumference)"
          fill="transparent"
          :transform="returnCircleTransformValue(index)"
        />
        <text
          v-if="segmentBigEnough(value)"
          text-anchor="middle"
          dy="3px"
          :x="chartData[index].textX"
          :y="chartData[index].textY"
        >{{ percentageString(value) }}</text>
      </g>
    </svg>
    <div class="legends">
      <div class="legend" v-for="(value, index) in sortedValues" v-bind:key="index">
        <span class="color" :style="{ 'background-color': colors[index] }"></span>
        <span class="label">{{ labels[index] }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: ['values'],
  data () {
    return {
      angleOffset: -90,
      chartData: [],
      cx: 100,
      cy: 100,
      radius: 100 - 25,
      sortedValues: [],
      strokeWidth: 50,
      colors: [],
      initialValues: [],
      labels: []
    }
  },
  computed: {
    // adjust the circumference to add small white gaps
    adjustedCircumference () {
      return this.circumference - 2
    },
    circumference () {
      return 2 * Math.PI * this.radius
    },
    dataTotal () {
      return this.sortedValues.reduce((acc, val) => acc + val)
    }
  },
  methods: {
    sortInitialValues () {
      return (this.sortedValues = this.initialValues) // Expect to be sorted for now (this.sortedValues = this.initialValues.sort((a, b) => b - a))
    },
    calculateChartData () {
      this.sortedValues.forEach((dataVal, index) => {
        const { x, y } = this.calculateTextCoords(dataVal, this.angleOffset)
        // start at -90deg so that the largest segment is perpendicular to top
        const data = {
          degrees: this.angleOffset,
          textX: x,
          textY: y
        }
        this.chartData.push(data)
        this.angleOffset =
          this.dataPercentage(dataVal) * 360 + this.angleOffset
      })
    },
    calculateStrokeDashOffset (dataVal, circumference) {
      const strokeDiff = this.dataPercentage(dataVal) * circumference
      return circumference - strokeDiff
    },
    calculateTextCoords (dataVal, angleOffset) {
      // t must be radians
      // x(t) = r cos(t) + j
      // y(t) = r sin(t) + j

      const angle = (this.dataPercentage(dataVal) * 360) / 2 + angleOffset
      const radians = this.degreesToRadians(angle)

      const textCoords = {
        x: this.radius * Math.cos(radians) + this.cx,
        y: this.radius * Math.sin(radians) + this.cy
      }
      return textCoords
    },
    degreesToRadians (angle) {
      return angle * (Math.PI / 180)
    },
    dataPercentage (dataVal) {
      return dataVal / this.dataTotal
    },
    percentageString (dataVal) {
      return `${Math.round(this.dataPercentage(dataVal) * 100)}%`
    },
    returnCircleTransformValue (index) {
      return `rotate(${this.chartData[index].degrees}, ${this.cx}, ${this.cy})`
    },
    segmentBigEnough (dataVal) {
      return Math.round(this.dataPercentage(dataVal) * 100) > 5
    }
  },
  watch: {
    values: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal) {
          this.colors = this.values.map(d => d.color)
          this.initialValues = this.values.map(d => d.value)
          this.labels = this.values.map(d => d.label)

          this.sortInitialValues()
          this.calculateChartData()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.legends {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  .legend {
    margin-bottom: 0.5rem;
    display: flex;
    line-height: 1;
    font-size: 0.85em;
    .color {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 1em 1em;
      width: 1em;
      height: 1em;
      border-radius: 0.1em;
      margin-right: 0.5rem;
    }
    .label {
      font-weight: bold;
    }
  }
}
.donut-chart {
  text {
    fill: white;
    font-size: 1em;
    font-weight: bold;
  }
}
</style>
